/**
 * Shared copy aligned with docs/features database UX feedback (outcome-focused language).
 */
export const DATABASE_UI = {
  pageTitle_addSanitizedDb: 'Connect database',
  pageTitle_connectExisting: 'Connect existing database',
  button_addDatabase: 'Connect database',
  button_connect: 'Connect',
  button_defineDatabase: 'Define database',
  button_continueSetup: 'Continue setup',
  nav_addSanitizedDb: 'Connect database',
  step_databaseIdentity: 'Database identity',
  step_deploymentModel: 'How you connect',
  step_connectSource: 'Connect source database',
  step_connectExisting: 'Choose database',
  /** Formerly “Classify columns” — label columns before sanitization rules. */
  step_labelSensitiveColumns: 'Label sensitive columns',
  step_reviewSensitiveFields: 'Sanitize sensitive fields',
  step_reviewSensitiveFields_desc: 'Classify columns and choose how each field should be sanitized.',
  step_enableForks: 'Enable database forks',
  step_createFilters: 'Create filters',
  step_takeForkSnapshot: 'Take a sanitized snapshot',
  step_createSanitizedSeed: 'Create sanitized seed data',
  step_createSanitizedSeed_desc:
    'Create sanitized data from this database, or upload a dump, and load it into your project’s preview environment.',
  hosting_hostedByVeilStream: 'VeilStream-hosted proxy',
  hosting_selfHostedProxy: 'Self-hosted proxy',
  deploymentModel_hostedDescription:
    'VeilStream hosts a proxy that filters data on the way to your Postgres. You provide a connection URI reachable from our cloud.',
  deploymentModel_selfHostedDescription:
    'You run our proxy in your infrastructure. VeilStream receives schema metadata and policy — not your upstream DATABASE_URI.',
  deploymentModel_hostedRecommended:
    'Recommended for: previews · demos · fast setup',
  deploymentModel_selfHostedRecommended:
    'Recommended for: production data · regulated workloads · private networks',
  connectOption_existing: 'Existing database',
  connectOption_existing_desc: 'Connect a database already defined in this organization.',
  connectionUri_label: 'Postgres connection URI',
  connectionUri_hintReadOnly:
    'Use a restricted read-only user — not an admin connection string.',
  connectionUri_hintTls: 'Use sslmode=require in the URI when your provider expects TLS.',
  gateway_readOnlyUrl_title: 'Read-only URL',
  gateway_readOnlyUrl_caption:
    'Sanitized read access through the VeilStream read gateway. Mutating SQL is rejected.',
  gateway_readOnlyUrl_copyLabel: 'Copy read-only URL',
  gateway_writeApprovedUrl_title: 'Read/write-approved URL',
  gateway_writeApprovedUrl_caption:
    'Connect through the VeilStream write-approval gateway. Mutating SQL is held until a reviewer approves it, then runs on your write upstream.',
  gateway_writeApprovedUrl_disabledCaption:
    'Write-approval gateway host. Enable approved writes in the upstream URIs section to allow mutating SQL (held for reviewer approval).',
  gateway_writeApprovedUrl_copyLabel: 'Copy read/write-approved URL',
  gateway_writeApprovedUrl_configureUpstreamHint:
    'Configure a write upstream connection URI in the upstream URIs section before using the read/write-approved URL.',
  gateway_writeApprovedUrl_enableHint:
    'Turn on Enable approved writes in the upstream URIs section after configuring a primary connection URI.',
  connection_upstreamSection_overline: 'Operator',
  connection_upstreamSection_title: 'Upstream URIs',
  connection_upstreamSection_description:
    'Postgres connections VeilStream uses to introspect schema, serve sanitized reads, and execute approved writes. Not for client applications.',
  connection_downstreamSection_overline: 'Client access',
  connection_downstreamSection_title: 'Downstream URIs',
  connection_downstreamSection_description:
    'Connection strings for applications and users connecting through the VeilStream psql-gateway.',
  security_readOnlyUserNote:
    'Use a restricted read-only database user. VeilStream uses this connection to introspect schema and create sanitized access paths. Do not use an admin connection string.',
  security_egressIpsBullet: (dcLabel: string, ips: string) =>
    `Allowlist VeilStream ${dcLabel} egress on your firewall: ${ips}.`,
  security_egressIpsTooltip: (dcLabel: string, ips: string) =>
    `If your database firewall restricts by IP, allow inbound connections from VeilStream ${dcLabel} egress: ${ips}.`,
  mode_immutableNote:
    'Connection mode cannot be changed after creation because it determines where connection secrets are stored and how the proxy is trusted. To use a different mode, add a new database definition.',
  selfHosted_securityModel:
    'Your upstream Postgres URI is stored only in your self-hosted proxy configuration. VeilStream stores schema metadata, sanitization policy, roles, and audit events.',
  locked_untilIntrospection: 'Locked until VeilStream has introspected your schema.',
  locked_untilRoleOrFilter: 'Locked until at least one sanitized role or filter profile exists.',
  locked_needDatabaseIdentityFirst: 'Complete database identity first, or choose an existing database.',
  locked_needConnectUriOrProxySchema:
    'Finish Connect source database first — hosted URI + introspection, or self-hosted proxy publishing schema.',
  locked_needSensitivityLabelsSaved: 'Finish label sensitive columns and save labels before setting sanitization rules.',
  status_continueSetupHint: 'Connection or schema onboarding not finished',

  semantic_success: 'Ready',
  semantic_warningNeedsAction: 'Needs setup',
  semantic_neutralIncomplete: 'In progress',

  tab_detailedRules: 'Data Rules',

  /** Rules canvas / graphical schema (database detail, rules hub, onboarding). Detail tab shows as “Schema”. */
  view_schemaDiagram: 'Schema',

  existingDatabase_choose:
    'Choose a VeilStream database that is already set up in this organization. No connection URI is needed when schema is available.',
  existingDatabase_ready:
    'This database is already connected and has schema metadata. Continue to configure seed data.',
  existingDatabase_needsConnection: 'Needs connection setup',
  existingDatabase_readyLabel: 'Ready',
  seedSourceUser_label: 'Database user',
  seedSourceUser_choose:
    'Choose which database user VeilStream should use for preview data snapshots and sanitization rules.',
  seedSourceUser_none:
    'No database users have access to this database yet. Assign a user to a role under Databases, then refresh.',
  seedSourceUser_loading: 'Loading database users…',
  seed_needComposeRepo:
    'Configure a compose repository for this project before taking a seed snapshot. Seed data is attached to the compose Postgres service in preview environments.',
  button_configureComposeRepo: 'Configure compose repository',
  button_openDatabase: 'Open database',
  button_backToProject: 'Back to project',
} as const

export const PROJECT_UI = {
  locked_needProjectNamed: 'Finish naming your project before linking repositories.',
  locked_needReposLinked: 'Finish linking repositories before configuring services.',
  locked_configureServicesFirst: 'Configure services and mappings before attaching seed data.',
  repo_troubleshoot:
    'If you do not see a repository, confirm the VeilStream GitHub app is installed on that org/account, or switch the owner picker.',
  exposure_helper:
    'Disabled removes routing. Internal is cluster-only; External adds an edge URL. Exactly one Primary service receives your project hostname.',
  postOnboarding_heading: 'What would you like to do next?',
  previewData_title: 'Preview data',
  previewData_unconfiguredBody:
    'Connect a sanitized source database and choose snapshots for your compose Postgres service',
  previewData_configuredBody: 'Preview environments restore sanitized snapshots into',
  previewData_configure: 'Configure preview data',
  previewData_manage: 'Manage preview data',
  previewData_configureServiceFirst: 'Configure Postgres service',
  previewData_addSourceDatabase: 'Connect database',
  previewData_connectExisting: 'Connect existing',
  previewData_noExistingDatabases:
    'No VeilStream databases in this organization yet. Use Add to define one first.',
} as const